import { inject, Injectable, InjectionToken } from '@angular/core';
import { combineLatest, debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';

import { PropsOf } from '@blaze-runtime/engine-domain';
import { VisibilityExpressionState } from '@blaze-shared/datasets-domain';
import {
  PropertiesConfigInputVisibilityForGroupsModel,
  RuntimeContext,
  VisibilityConditionWithRowMatchStrategy,
} from '@blaze-shared/elements-domain';
import { DatasetColumn } from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

export type EvaluateExpressionFn = (
  expressionObj: any,
  context: RuntimeContext,
  defaultValue: boolean,
) => boolean;
export type NormalizeLinkedParamsFn = (context: RuntimeContext) => RuntimeContext;

export const EVALUATE_EXPRESSION_FN = new InjectionToken<EvaluateExpressionFn>(
  'EVALUATE_EXPRESSION_FN',
);
export const NORMALIZE_LINKED_PARAMS_FN = new InjectionToken<NormalizeLinkedParamsFn>(
  'NORMALIZE_LINKED_PARAMS_FN',
);

interface ColumnsVisibilityConditionGroup {
  conditions: VisibilityConditionWithRowMatchStrategy[];
  states: VisibilityExpressionState[];
  groupColumns: string[];
  columnsDataset: (DatasetColumn | undefined)[];
  valueDataset: (DatasetColumn | undefined)[];
}

@Injectable()
export class TableColumnsVisibilityService {
  private readonly evaluateExpressionFn = inject(EVALUATE_EXPRESSION_FN);
  private readonly normalizeLinkedParamsFn = inject(NORMALIZE_LINKED_PARAMS_FN);

  initializeExpressionContextForColumnVisibility(
    runtimeContext$: Observable<RuntimeContext>,
    props$: Observable<PropsOf<'table'> | undefined>,
    data$: Observable<Record<string, any>[]>,
  ): Observable<string[]> {
    return combineLatest({
      context: runtimeContext$,
      props: props$.pipe(
        map((props) => props?.content.columnsVisibility?.visible),
        distinctUntilChanged(isDeepEqual),
      ),
      data: data$,
    }).pipe(
      debounceTime(300),
      map(({ context, props, data }) => {
        const normalizedContext = this.normalizeLinkedParamsFn(context);
        const invisibleColumns = this.getInvisibleColumns(normalizedContext, props, data);
        return invisibleColumns;
      }),
    );
  }

  private getInvisibleColumns(
    runtimeContext: RuntimeContext,
    columnsVisibilityProps: PropertiesConfigInputVisibilityForGroupsModel | undefined,
    data: any[],
  ): string[] {
    const modelGroups = columnsVisibilityProps?.groups;
    if (!modelGroups?.length) {
      return [];
    }

    const groups: ColumnsVisibilityConditionGroup[] = modelGroups
      .filter((g) => g.conditions?.length && g.states?.length && g.groupColumns?.length)
      .map((g) => ({
        conditions: g.conditions as NonNullable<typeof g.conditions>,
        states: g.states,
        groupColumns: g.groupColumns,
        columnsDataset: g.datasetColumns?.map((d) => d?.columns) ?? [],
        valueDataset: g.datasetColumns?.map((d) => d?.value) ?? [],
      }));

    const invisibleColumns: string[] = [];

    for (const group of groups) {
      const groupState = group.states[0]; // 'hide' or 'show'
      const groupResult = this.evaluateConditionGroup(group, runtimeContext, data);

      if (groupState === 'hide' && groupResult) {
        invisibleColumns.push(...group.groupColumns);
      } else if (groupState === 'show' && !groupResult) {
        invisibleColumns.push(...group.groupColumns);
      }
    }

    return invisibleColumns;
  }
  private evaluateConditionGroup(
    group: ColumnsVisibilityConditionGroup,
    runtimeContext: RuntimeContext,
    data: any[],
  ): boolean {
    // Evaluate each condition individually, respecting its rowMatchStrategy
    const results = group.conditions.map((condition, index) => {
      const columnsDataset = group.columnsDataset[index];
      const valueDataset = group.valueDataset[index];
      return this.evaluateSingleCondition(
        condition,
        runtimeContext,
        data,
        columnsDataset,
        valueDataset,
      );
    });

    // states: ['hide'|'show', 'and', 'or', 'and', ...]
    // Combine with proper precedence: AND binds tighter than OR
    // Split into OR-separated sub-groups, each connected by AND
    // e.g. [R0, R1, R2, R3] with states [hide, and, or, and]
    //   → (R0 AND R1) OR (R2 AND R3)
    const orGroups: boolean[][] = [[]];

    for (let i = 0; i < results.length; i++) {
      const state = group.states[i];
      if (state === 'or') {
        orGroups.push([]);
      }
      const lastGroup = orGroups.at(-1);
      if (!lastGroup) {
        throw new Error('Invalid state: OR group is not defined');
      }
      lastGroup.push(results[i]);
    }

    return orGroups.some((andGroup) => andGroup.every(Boolean));
  }

  private evaluateSingleCondition(
    condition: VisibilityConditionWithRowMatchStrategy,
    runtimeContext: RuntimeContext,
    rows: any[],
    columnsDataset?: DatasetColumn,
    valueDataset?: DatasetColumn,
  ): boolean {
    const hasRowContext =
      condition.columns.some((col) => col.startsWith('$$row')) ||
      condition.value?.includes('$$row');

    if (!hasRowContext) {
      const conditionToExecute = [
        { ...condition, columns: condition.columns[0], state: 'hide' as const },
      ];
      return !this.evaluateExpressionFn(conditionToExecute, runtimeContext, true);
    }

    const rowMatchStrategy = condition.rowMatchStrategy ?? 'any-row';
    const expressionObj = [
      {
        ...condition,
        columns: condition.columns[0],
        state: 'hide' as const,
        columnsDataset,
        valueDataset,
      },
    ];

    if (!rows.length) {
      return false;
    }

    if (rowMatchStrategy === 'all-rows') {
      return rows.every((row) => {
        const rowContext = { ...runtimeContext, row };
        return !this.evaluateExpressionFn(expressionObj, rowContext, true);
      });
    }

    // 'any-row' (default) — evaluate against every context but return true if any evaluation returns true
    return rows.some((row) => {
      const rowContext = { ...runtimeContext, row };
      return !this.evaluateExpressionFn(expressionObj, rowContext, true);
    });
  }
}
